import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../Sheet';
import { Button } from '../Button';
import { Label } from '../Label';
import { Checkbox } from '../Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Select';
import { Input } from '../Input';
import { Badge } from '../Badge';
import { Separator } from '../Separator';
import { Filter as FilterIcon, Calendar, MapPin, Globe, Tag, Activity, ShieldAlert, RotateCcw, Check, X } from 'lucide-react';
export type FilterState = {
  timeRange: string;
  customFrom?: string;
  customTo?: string;
  regions: string[];
  platforms: string[];
  classifications: string[];
  sentiments: string[];
  statuses: string[];
  riskLevels: string[];
  keyword: string;
};
export const DEFAULT_FILTERS: FilterState = {
  timeRange: '24h',
  regions: [],
  platforms: [],
  classifications: [],
  sentiments: [],
  statuses: [],
  riskLevels: [],
  keyword: ''
};
const TIME_RANGES = [{
  id: '1h',
  label: '1 Jam Terakhir'
}, {
  id: '24h',
  label: '24 Jam Terakhir'
}, {
  id: '7d',
  label: '7 Hari Terakhir'
}, {
  id: '30d',
  label: '30 Hari Terakhir'
}, {
  id: '90d',
  label: '90 Hari Terakhir'
}, {
  id: 'custom',
  label: 'Rentang Kustom...'
}];
const REGIONS = ['Nasional', 'Sumatera', 'Jawa', 'Kalimantan', 'Sulawesi', 'Bali & Nusa Tenggara', 'Maluku', 'Papua'];
const PLATFORMS = ['X (Twitter)', 'Facebook', 'Instagram', 'TikTok', 'YouTube', 'Telegram', 'WhatsApp', 'Media Online'];
const CLASSIFICATIONS = ['IDEOLOGI', 'POLITIK', 'EKONOMI', 'SOSIAL', 'BUDAYA', 'PERTAHANAN', 'KEAMANAN'];
const SENTIMENTS = [{
  id: 'positif',
  label: 'Positif',
  color: 'emerald'
}, {
  id: 'netral',
  label: 'Netral',
  color: 'slate'
}, {
  id: 'negatif',
  label: 'Negatif',
  color: 'red'
}];
const STATUSES = [{
  id: 'ORGANIK',
  label: 'Organik'
}, {
  id: 'TERKOORDINASI',
  label: 'Terkoordinasi'
}, {
  id: 'BOT',
  label: 'Bot Activity'
}, {
  id: 'FOREIGN',
  label: 'Foreign Influence'
}];
const RISK_LEVELS = [{
  id: 'rendah',
  label: 'Rendah',
  color: 'emerald'
}, {
  id: 'sedang',
  label: 'Sedang',
  color: 'amber'
}, {
  id: 'tinggi',
  label: 'Tinggi',
  color: 'orange'
}, {
  id: 'kritis',
  label: 'Kritis',
  color: 'red'
}];
export function FilterSheet({
  open,
  onOpenChange,
  value,
  onApply,
  pageTitle
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: FilterState;
  onApply: (next: FilterState) => void;
  pageTitle?: string;
}) {
  const [draft, setDraft] = useState<FilterState>(value);
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);
  const toggle = (key: keyof FilterState, item: string) => {
    setDraft(prev => {
      const arr = prev[key] as string[] ?? [];
      const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
      return {
        ...prev,
        [key]: next
      };
    });
  };
  const activeCount = countActive(draft);
  return <Sheet open={open} onOpenChange={onOpenChange} data-id="element-363">
      <SheetContent className="w-full sm:max-w-md bg-background border-border/60 p-0 flex flex-col" data-id="element-364">
        <SheetHeader className="p-6 pb-4 border-b border-border/60" data-id="element-365">
          <div className="flex items-center gap-2 mb-1" data-id="element-366">
            <div className="size-7 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center" data-id="element-367">
              <FilterIcon className="size-3.5 text-primary" data-id="element-368" />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-369">
              Filter Lanjutan
            </span>
            {activeCount > 0 && <Badge className="bg-primary/10 text-primary border-primary/20 font-mono text-[10px]" data-id="element-370">
                {activeCount} aktif
              </Badge>}
          </div>
          <SheetTitle className="text-lg" data-id="element-371">
            {pageTitle ? `Filter · ${pageTitle}` : 'Filter Data'}
          </SheetTitle>
          <SheetDescription data-id="element-372">
            Persempit data sesuai konteks operasi. Dapat dikombinasikan.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6" data-id="element-373">
          {/* Keyword */}
          <Group icon={Tag} label="Kata Kunci" data-id="element-374">
            <Input value={draft.keyword} onChange={e => setDraft({
            ...draft,
            keyword: e.target.value
          })} placeholder="Cari kata, frasa, hashtag, atau handle..." className="bg-muted/30 border-border/60" data-id="element-375" />
          </Group>

          {/* Time range */}
          <Group icon={Calendar} label="Rentang Waktu" data-id="element-376">
            <Select value={draft.timeRange} onValueChange={v => setDraft({
            ...draft,
            timeRange: v
          })} data-id="element-377">
              <SelectTrigger className="bg-muted/30 border-border/60" data-id="element-378">
                <SelectValue data-id="element-379" />
              </SelectTrigger>
              <SelectContent data-id="element-380">
                {TIME_RANGES.map(r => <SelectItem key={r.id} value={r.id} data-id="element-381">
                    {r.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            {draft.timeRange === 'custom' && <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-382">
                <div data-id="element-383">
                  <Label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-384">
                    Dari
                  </Label>
                  <Input type="date" value={draft.customFrom ?? ''} onChange={e => setDraft({
                ...draft,
                customFrom: e.target.value
              })} className="bg-muted/30 border-border/60 mt-1" data-id="element-385" />
                </div>
                <div data-id="element-386">
                  <Label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-387">
                    Sampai
                  </Label>
                  <Input type="date" value={draft.customTo ?? ''} onChange={e => setDraft({
                ...draft,
                customTo: e.target.value
              })} className="bg-muted/30 border-border/60 mt-1" data-id="element-388" />
                </div>
              </div>}
          </Group>

          {/* Region */}
          <Group icon={MapPin} label="Wilayah" count={draft.regions.length} data-id="element-389">
            <div className="grid grid-cols-2 gap-1.5" data-id="element-390">
              {REGIONS.map(r => <Toggleable key={r} label={r} active={draft.regions.includes(r)} onClick={() => toggle('regions', r)} data-id="element-391" />)}
            </div>
          </Group>

          {/* Platform */}
          <Group icon={Globe} label="Platform" count={draft.platforms.length} data-id="element-392">
            <div className="grid grid-cols-2 gap-1.5" data-id="element-393">
              {PLATFORMS.map(p => <Toggleable key={p} label={p} active={draft.platforms.includes(p)} onClick={() => toggle('platforms', p)} data-id="element-394" />)}
            </div>
          </Group>

          {/* Classification */}
          <Group icon={Tag} label="Klasifikasi IPOLEKSOSBUDHANKAM" count={draft.classifications.length} data-id="element-395">
            <div className="flex flex-wrap gap-1.5" data-id="element-396">
              {CLASSIFICATIONS.map(c => <Chip key={c} label={c} active={draft.classifications.includes(c)} onClick={() => toggle('classifications', c)} data-id="element-397" />)}
            </div>
          </Group>

          {/* Sentiment */}
          <Group icon={Activity} label="Sentimen" count={draft.sentiments.length} data-id="element-398">
            <div className="flex gap-1.5" data-id="element-399">
              {SENTIMENTS.map(s => <Chip key={s.id} label={s.label} tone={s.color as any} active={draft.sentiments.includes(s.id)} onClick={() => toggle('sentiments', s.id)} data-id="element-400" />)}
            </div>
          </Group>

          {/* Status */}
          <Group icon={ShieldAlert} label="Status" count={draft.statuses.length} data-id="element-401">
            <div className="flex flex-wrap gap-1.5" data-id="element-402">
              {STATUSES.map(s => <Chip key={s.id} label={s.label} active={draft.statuses.includes(s.id)} onClick={() => toggle('statuses', s.id)} data-id="element-403" />)}
            </div>
          </Group>

          {/* Risk Level */}
          <Group icon={ShieldAlert} label="Tingkat Risiko" count={draft.riskLevels.length} data-id="element-404">
            <div className="flex gap-1.5" data-id="element-405">
              {RISK_LEVELS.map(r => <Chip key={r.id} label={r.label} tone={r.color as any} active={draft.riskLevels.includes(r.id)} onClick={() => toggle('riskLevels', r.id)} data-id="element-406" />)}
            </div>
          </Group>
        </div>

        <div className="border-t border-border/60 p-4 flex gap-2 bg-card/50" data-id="element-407">
          <Button variant="outline" className="flex-1" onClick={() => setDraft(DEFAULT_FILTERS)} data-id="element-408">
            <RotateCcw className="size-3.5 mr-1.5" data-id="element-409" /> Reset
          </Button>
          <Button className="flex-[1.4]" onClick={() => {
          onApply(draft);
          onOpenChange(false);
        }} data-id="element-410">
            <Check className="size-3.5 mr-1.5" data-id="element-411" />
            Terapkan ({activeCount})
          </Button>
        </div>
      </SheetContent>
    </Sheet>;
}
function Group({
  icon: Icon,
  label,
  count,
  children
}: {
  icon: any;
  label: string;
  count?: number;
  children: React.ReactNode;
}) {
  return <div data-id="element-412">
      <div className="flex items-center gap-2 mb-2" data-id="element-413">
        <Icon className="size-3.5 text-muted-foreground" data-id="element-414" />
        <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-415">
          {label}
        </Label>
        {!!count && <Badge variant="outline" className="font-mono text-[9px] h-4 px-1.5 border-primary/30 text-primary bg-primary/5" data-id="element-416">
            {count}
          </Badge>}
      </div>
      {children}
    </div>;
}
function Toggleable({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`flex items-center gap-2 px-2.5 py-2 rounded-md border text-left text-xs transition ${active ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/20 border-border/60 hover:bg-muted/40 hover:border-border text-foreground/80'}`} data-id="element-417">
      <Checkbox checked={active} className="pointer-events-none" data-id="element-418" />
      <span className="truncate" data-id="element-419">{label}</span>
    </button>;
}
function Chip({
  label,
  active,
  onClick,
  tone = 'primary'
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: 'primary' | 'red' | 'amber' | 'orange' | 'emerald' | 'slate';
}) {
  const tones: Record<string, {
    active: string;
    inactive: string;
  }> = {
    primary: {
      active: 'bg-primary/10 border-primary/50 text-primary',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    },
    red: {
      active: 'bg-red-50 border-red-300 text-red-700',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    },
    amber: {
      active: 'bg-amber-50 border-amber-300 text-amber-700',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    },
    orange: {
      active: 'bg-orange-50 border-orange-300 text-orange-700',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    },
    emerald: {
      active: 'bg-emerald-50 border-emerald-300 text-emerald-700',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    },
    slate: {
      active: 'bg-slate-100 border-slate-300 text-slate-700',
      inactive: 'bg-muted/30 border-border/60 text-foreground/80'
    }
  };
  return <button type="button" onClick={onClick} className={`px-2.5 py-1 rounded-md border font-mono text-[10px] uppercase tracking-wider transition hover:border-current/60 ${active ? tones[tone].active : tones[tone].inactive}`} data-id="element-420">
      {label}
    </button>;
}
export function countActive(f: FilterState) {
  return (f.regions?.length ?? 0) + (f.platforms?.length ?? 0) + (f.classifications?.length ?? 0) + (f.sentiments?.length ?? 0) + (f.statuses?.length ?? 0) + (f.riskLevels?.length ?? 0) + (f.keyword ? 1 : 0) + (f.timeRange !== '24h' ? 1 : 0);
}
export function FilterChips({
  filters,
  onClear,
  onClearAll
}: {
  filters: FilterState;
  onClear: (key: keyof FilterState, value?: string) => void;
  onClearAll: () => void;
}) {
  const chips: Array<{
    key: keyof FilterState;
    value?: string;
    label: string;
  }> = [];
  if (filters.keyword) chips.push({
    key: 'keyword',
    label: `“${filters.keyword}”`
  });
  if (filters.timeRange !== '24h') {
    const t = TIME_RANGES.find(x => x.id === filters.timeRange);
    if (t) chips.push({
      key: 'timeRange',
      label: t.label
    });
  }
  filters.regions.forEach(r => chips.push({
    key: 'regions',
    value: r,
    label: r
  }));
  filters.platforms.forEach(p => chips.push({
    key: 'platforms',
    value: p,
    label: p
  }));
  filters.classifications.forEach(c => chips.push({
    key: 'classifications',
    value: c,
    label: c
  }));
  filters.sentiments.forEach(s => chips.push({
    key: 'sentiments',
    value: s,
    label: s
  }));
  filters.statuses.forEach(s => chips.push({
    key: 'statuses',
    value: s,
    label: s
  }));
  filters.riskLevels.forEach(r => chips.push({
    key: 'riskLevels',
    value: r,
    label: r
  }));
  if (chips.length === 0) return null;
  return <div className="flex items-center flex-wrap gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2" data-id="element-421">
      <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold mr-1" data-id="element-422">
        {chips.length} Filter Aktif:
      </span>
      {chips.map((c, i) => <button key={`${c.key}-${c.value ?? ''}-${i}`} onClick={() => onClear(c.key, c.value)} className="inline-flex items-center gap-1 rounded bg-card border border-border/60 hover:border-red-300 hover:bg-red-50 hover:text-red-700 px-1.5 py-0.5 font-mono text-[10px] transition group" data-id="element-423">
          {c.label}
          <X className="size-2.5 opacity-50 group-hover:opacity-100" data-id="element-424" />
        </button>)}
      <button onClick={onClearAll} className="ml-auto font-mono text-[10px] text-primary hover:underline" data-id="element-425">
        Hapus semua
      </button>
    </div>;
}